import { z } from 'zod';
import { paginationQuery } from '../api.js';
/**
 * Verträge mit elektronischer Signatur (Modul `contracts`, siehe docs/vertraege.md). Ein Vertrag entsteht in Wirkia aus einer Vorlage oder freiem Text, das Unternehmen unterschreibt
 * zuerst, der Vertragspartner (Mitarbeiter, Kontakt oder Dritter) bekommt das PDF und einen Link und unterschreibt online mit einem Code, der an seine E-Mail-Adresse geht.
 * Jeder Schritt steht in einem Verlauf, dessen Einträge verkettet sind. Am Ende gibt es ein PDF mit Signaturprotokoll und seinen Hash als Nachweis.
 *
 * Rechtlich ist das eine einfache bis fortgeschrittene elektronische Signatur nach eIDAS (Art. 25 Abs. 1 und Art. 26): Sie ist vor Gericht als Beweismittel zugelassen und wird frei
 * gewürdigt. Sie ersetzt nicht die Schriftform (§ 126 BGB). Wo das Gesetz die Schriftform verlangt (Kündigung und Befristung eines Arbeitsvertrags, § 623 BGB und § 14 Abs. 4 TzBfG),
 * braucht es die eigenhändige Unterschrift oder eine qualifizierte elektronische Signatur.
 */
export const CONTRACT_KINDS = ['employment', 'service', 'nda', 'other'];
export const CONTRACT_KIND_LABELS = {
    employment: 'Arbeitsvertrag',
    service: 'Dienstleistungsvertrag',
    nda: 'Geheimhaltungsvereinbarung',
    other: 'Sonstiger Vertrag',
};
export const CONTRACT_STATUSES = ['draft', 'sent', 'signed', 'declined', 'expired', 'void'];
export const CONTRACT_STATUS_LABELS = {
    draft: 'Entwurf',
    sent: 'Zur Unterschrift gesendet',
    signed: 'Digital E-signiert',
    declined: 'Abgelehnt',
    expired: 'Abgelaufen',
    void: 'Zurückgezogen',
};
export const CONTRACT_PARTY_KINDS = ['employee', 'contact', 'other'];
export const CONTRACT_PARTY_LABELS = { employee: 'Mitarbeiter', contact: 'Kontakt', other: 'Anderer Vertragspartner' };
export const CONTRACT_EVENT_KINDS = ['sent', 'viewed', 'code_requested', 'code_failed', 'signed_company', 'signed_party', 'declined', 'void', 'expired', 'reminded', 'downloaded', 'filed', 'termination_issued', 'termination_viewed', 'termination_code_requested', 'termination_code_failed', 'termination_acknowledged', 'termination_delivered', 'termination_withdrawn'];
export const CONTRACT_EVENT_LABELS = {
    sent: 'Zur Unterschrift gesendet',
    viewed: 'Vom Vertragspartner geöffnet',
    code_requested: 'Bestätigungscode angefordert',
    code_failed: 'Falscher Bestätigungscode eingegeben',
    signed_company: 'Vom Unternehmen digital e-signiert',
    signed_party: 'Vom Vertragspartner digital e-signiert',
    declined: 'Vom Vertragspartner abgelehnt',
    void: 'Zurückgezogen',
    expired: 'Abgelaufen',
    reminded: 'Erinnerung gesendet',
    downloaded: 'E-signiertes PDF heruntergeladen',
    filed: 'In Dokumente abgelegt',
    termination_issued: 'Kündigung freigegeben',
    termination_viewed: 'Kündigung vom Empfänger geöffnet',
    termination_code_requested: 'Bestätigungscode für die Kündigung angefordert',
    termination_code_failed: 'Falscher Code bei der Kündigung eingegeben',
    termination_acknowledged: 'Empfang der Kündigung bestätigt',
    termination_delivered: 'Zugang der Kündigung festgehalten',
    termination_withdrawn: 'Kündigung zurückgezogen',
};
/** Die Erklärung, die der Vertragspartner mit der Unterschrift abgibt. Sie steht im Protokoll und im PDF, wortgleich. */
export const SIGNATURE_STATEMENT = 'Ich habe den Vertrag vollständig gelesen und stimme ihm zu. Ich gebe diese Erklärung selbst ab und bestätige sie mit dem Code, den ich an meine E-Mail-Adresse erhalten habe. Mir ist bewusst, dass diese elektronische Unterschrift verbindlich ist.';
/** Wie lange ein Bestätigungscode gilt, in Minuten, und wie oft er falsch eingegeben werden darf */
export const CONTRACT_CODE_MINUTES = 15;
export const CONTRACT_CODE_ATTEMPTS = 5;
/** Platzhalter in Vertragstexten. Sie werden beim Anlegen und beim Senden mit den Angaben der Beteiligten ersetzt. */
export const CONTRACT_PLACEHOLDERS = [
    { key: 'partner_name', label: 'Name des Vertragspartners' },
    { key: 'partner_email', label: 'E-Mail des Vertragspartners' },
    { key: 'firma_name', label: 'Name Ihres Unternehmens' },
    { key: 'firma_anschrift', label: 'Anschrift Ihres Unternehmens' },
    { key: 'datum', label: 'Heutiges Datum' },
];
export function fillContractPlaceholders(text, values) {
    return text.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (whole, key) => values[key] ?? whole);
}
const FOOTER = '\n\nOrt, Datum und Unterschriften stehen auf der letzten Seite im Protokoll der elektronischen Unterschrift.';
/** Mustertexte als Ausgangspunkt. Sie ersetzen keine Rechtsberatung, die Seite weist beim Anlegen darauf hin. */
export const CONTRACT_TEMPLATES = [
    {
        key: 'service',
        kind: 'service',
        title: 'Dienstleistungsvertrag',
        body: 'Zwischen {{firma_name}}, {{firma_anschrift}} (Auftraggeber)\nund {{partner_name}} (Auftragnehmer)\nwird am {{datum}} folgender Vertrag geschlossen.\n\n' +
            '§ 1 Leistung\nDer Auftragnehmer erbringt für den Auftraggeber folgende Leistungen: [Leistung beschreiben].\n\n' +
            '§ 2 Vergütung\nDie Vergütung beträgt [Betrag] € zuzüglich gesetzlicher Umsatzsteuer. Rechnungen sind innerhalb von 14 Tagen nach Zugang zu bezahlen.\n\n' +
            '§ 3 Laufzeit und Kündigung\nDer Vertrag beginnt am [Datum] und läuft auf unbestimmte Zeit. Er kann mit einer Frist von [Frist] zum Monatsende gekündigt werden.\n\n' +
            '§ 4 Vertraulichkeit\nBeide Parteien behandeln alle nicht öffentlichen Informationen der anderen Partei vertraulich.\n\n' +
            '§ 5 Schlussbestimmungen\nÄnderungen und Ergänzungen bedürfen der Textform. Sollte eine Bestimmung unwirksam sein, bleibt der Vertrag im Übrigen wirksam.' + FOOTER,
    },
    {
        key: 'nda',
        kind: 'nda',
        title: 'Geheimhaltungsvereinbarung',
        body: 'Zwischen {{firma_name}}, {{firma_anschrift}}\nund {{partner_name}}\nwird am {{datum}} folgende Vereinbarung geschlossen.\n\n' +
            '§ 1 Gegenstand\nDie Parteien tauschen im Rahmen von [Zweck] vertrauliche Informationen aus. Vertraulich ist alles, was als vertraulich gekennzeichnet ist oder nach den Umständen als vertraulich zu behandeln ist.\n\n' +
            '§ 2 Pflichten\nDie empfangende Partei nutzt vertrauliche Informationen nur für den genannten Zweck, gibt sie nicht an Dritte weiter und schützt sie wie eigene vertrauliche Informationen.\n\n' +
            '§ 3 Dauer\nDie Pflichten gelten für die Dauer der Zusammenarbeit und [Zeitraum] darüber hinaus.\n\n' +
            '§ 4 Schlussbestimmungen\nÄnderungen bedürfen der Textform. Es gilt deutsches Recht.' + FOOTER,
    },
    {
        key: 'employment',
        kind: 'employment',
        title: 'Arbeitsvertrag',
        body: 'Zwischen {{firma_name}}, {{firma_anschrift}} (Arbeitgeber)\nund {{partner_name}} (Arbeitnehmer)\nwird am {{datum}} folgender Arbeitsvertrag geschlossen.\n\n' +
            '§ 1 Beginn und Tätigkeit\nDas Arbeitsverhältnis beginnt am [Datum]. Der Arbeitnehmer wird als [Tätigkeit] eingestellt.\n\n' +
            '§ 2 Arbeitszeit\nDie regelmäßige Arbeitszeit beträgt [Stunden] Stunden pro Woche. Die Lage der Arbeitszeit bestimmt der Arbeitgeber nach billigem Ermessen.\n\n' +
            '§ 3 Vergütung\nDie Vergütung beträgt [Betrag] € brutto pro Monat, zahlbar zum Monatsende.\n\n' +
            '§ 4 Urlaub\nDer Arbeitnehmer hat Anspruch auf [Anzahl] Arbeitstage Erholungsurlaub im Kalenderjahr.\n\n' +
            '§ 5 Probezeit und Kündigung\nDie ersten [Monate] Monate gelten als Probezeit. Die Kündigung bedarf der Schriftform (§ 623 BGB). Es gelten die gesetzlichen Kündigungsfristen, soweit nichts anderes vereinbart ist.\n\n' +
            '§ 6 Schlussbestimmungen\nÄnderungen und Ergänzungen bedürfen der Textform. Sollte eine Bestimmung unwirksam sein, bleibt der Vertrag im Übrigen wirksam.' + FOOTER,
    },
    { key: 'free', kind: 'other', title: 'Vertrag', body: 'Zwischen {{firma_name}}, {{firma_anschrift}}\nund {{partner_name}}\nwird am {{datum}} folgender Vertrag geschlossen.\n\n§ 1 [Gegenstand]\n\n§ 2 [Regelung]' + FOOTER },
];
/** Hinweis für Verträge, bei denen das Gesetz die Schriftform verlangt */
export const CONTRACT_FORM_WARNING = {
    employment: 'Ein Arbeitsvertrag ist formfrei und lässt sich elektronisch schließen. Eine Befristung dagegen und die Kündigung brauchen die Schriftform mit eigenhändiger Unterschrift (§ 14 Abs. 4 TzBfG, § 623 BGB). Für diese Teile reicht die elektronische Unterschrift in Wirkia nicht.',
    service: null,
    nda: null,
    other: 'Verlangt das Gesetz für Ihren Vertrag die Schriftform, ersetzt die elektronische Unterschrift in Wirkia sie nicht.',
};
const text = (min, max, label) => z.string().trim().min(min, `${label} ist zu kurz.`).max(max, `${label} ist zu lang.`);
const contractFields = {
    title: text(2, 160, 'Der Titel'),
    kind: z.enum(CONTRACT_KINDS),
    body: text(20, 60000, 'Der Vertragstext'),
    partyKind: z.enum(CONTRACT_PARTY_KINDS),
    employeeId: z.uuid().nullable().optional(),
    contactId: z.uuid().nullable().optional(),
    partyName: text(2, 160, 'Der Name'),
    partyEmail: z.string().trim().toLowerCase().max(200).pipe(z.email('Bitte geben Sie eine gültige E-Mail-Adresse an.')),
    /** Wie viele Tage der Link zur Unterschrift gilt, ab dem Senden */
    validDays: z.number().int().min(1).max(90).default(14),
};
export const createContractInput = z.object(contractFields);
export const updateContractInput = z.object(contractFields).partial().refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
/** Das Unternehmen unterschreibt mit seinem Passwort und sendet damit den Vertrag */
export const sendContractInput = z.object({ password: z.string().min(1, 'Bitte geben Sie Ihr Passwort ein.').max(200) });
export const voidContractInput = z.object({ reason: z.string().trim().max(500).optional() });
export const listContractsQuery = paginationQuery.extend({
    q: z.string().trim().max(100).optional(),
    status: z.enum(CONTRACT_STATUSES).optional(),
    kind: z.enum(CONTRACT_KINDS).optional(),
});
export const publicCodeInput = z.object({ name: text(2, 160, 'Der Name') });
export const publicSignInput = z.object({
    name: text(2, 160, 'Der Name'),
    code: z.string().trim().regex(/^\d{6}$/, 'Der Code besteht aus sechs Ziffern.'),
    consent: z.literal(true, { error: 'Bitte bestätigen Sie die Erklärung.' }),
});
export const publicDeclineInput = z.object({ reason: z.string().trim().max(500).optional() });
//# sourceMappingURL=contracts.js.map